import React, { useState, useEffect } from 'react';
import apiFetch from '@wordpress/api-fetch';
import { useToast } from '../../utils/Toast.jsx';
import { renderInputRow } from '../../utils/renderInputRow.js';

const StyleSettingsTab = () => {
  const toast = useToast();
  const [originalSettings, setOriginalSettings] = useState({});
  const [Settings, setSettings]                 = useState({});
  const [loading, setLoading]                   = useState(false);
  const [saved, setSaved]                       = useState(false);

  useEffect(() => {
    const global = window.DOTFW_GLOBAL_OPTIONS || {};
    setSettings(global);
    setOriginalSettings(global);
  }, []);

  const handleSave = () => {
    const changedData = Object.fromEntries(
      Object.entries(Settings).filter(([key, value]) => originalSettings[key] !== value)
    );
    if (Object.keys(changedData).length === 0) {
      toast.info('No changes detected.');
      return;
    }

    setLoading(true);
    setSaved(false);

    apiFetch({ path: '/wp/v2/settings', method: 'POST', data: changedData })
      .then(() => {
        toast.success('Style settings saved!');
        window.DOTFW_GLOBAL_OPTIONS = { ...(window.DOTFW_GLOBAL_OPTIONS || {}), ...Settings };
        setOriginalSettings(Settings);
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      })
      .catch((err) => {
        console.error('Error saving style settings:', err);
        toast.error('Error saving style settings.');
      })
      .finally(() => setLoading(false));
  };

  const updateSettings = (key, value) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="tab-root">

      {/* ── Loading overlay ── */}
      {loading && (
        <div className="spinner-overlay" aria-live="polite" aria-label="Saving">
          <div className="spinner-overlay-card">
            <span className="rotating-spinner" aria-hidden="true" />
            <span className="spinner-overlay-text">Saving changes…</span>
          </div>
        </div>
      )}

      {/* ── Section: Layout ── */}
      <div className="tab-section">
        <div className="tab-section-header">
          <div className="tab-section-meta">
            <h3 className="tab-section-title">Layout & Typography</h3>
            <p className="tab-section-sub">Control field style and font sizes</p>
          </div>
        </div>
        <div className="tab-section-body">
          {renderInputRow(
            'Predefined value style',
            'dotfw_donation_field_style',
            'select',
            [
              { label: 'Buttons',   value: 'dot_radio_btn' },
              { label: 'Dropdown',  value: 'dot_dropdown'  },
            ],
            '', Settings, updateSettings,
            'How predefined amounts are displayed to the customer'
          )}
          {renderInputRow(
            'Donation button font size',
            'dotfw_bot_btn_fontsize',
            'number', [], '',
            Settings, updateSettings,
            'In px'
          )}
          {renderInputRow(
            'Donation alert font size',
            'dotfw_bot_alt_fontsize',
            'number', [], '',
            Settings, updateSettings,
            'In px'
          )}
          {renderInputRow(
            'Remove button font size',
            'dotfw_remove_dot_btn_fontsize',
            'number', [], '',
            Settings, updateSettings,
            'In px'
          )}
          {renderInputRow(
            'Predefined value font size',
            'dotfw_predefined_val_fontsize',
            'number', [], '',
            Settings, updateSettings,
            'In px'
          )}
          {renderInputRow(
            'Predefined button padding',
            'dotfw_predefined_btn_tb_padding',
            'number', [], '',
            Settings, updateSettings,
            'Top / Bottom padding in px'
          )}
          {renderInputRow(
            '',
            'dotfw_predefined_btn_lr_padding',
            'number', [], '',
            Settings, updateSettings,
            'Left / Right padding in px'
          )}
        </div>
      </div>

      {/* ── Section: Donation Button Colors ── */}
      <div className="tab-section">
        <div className="tab-section-header">
          <div className="tab-section-meta">
            <h3 className="tab-section-title">Donation Button Colors</h3>
            <p className="tab-section-sub">Background, text and alert colors</p>
          </div>
        </div>
        <div className="tab-section-body">
          {renderInputRow(
            'Alert text color',
            'dotfw_dot_alt_color',
            'colorPicker', [], '',
            Settings, updateSettings
          )}
          {renderInputRow(
            'Donation button background color',
            'dotfw_dot_btn_bg_color',
            'colorPicker', [], '',
            Settings, updateSettings
          )}
          {renderInputRow(
            'Donation button text color',
            'dotfw_dot_btn_text_color',
            'colorPicker', [], '',
            Settings, updateSettings
          )}
          {renderInputRow(
            'Remove button background color',
            'dotfw_remove_dot_btn_bg_color',
            'colorPicker', [], '',
            Settings, updateSettings
          )}
          {renderInputRow(
            'Remove button text color',
            'dotfw_remove_dot_btn_text_color',
            'colorPicker', [], '',
            Settings, updateSettings
          )}
        </div>
      </div>

      {/* ── Section: Predefined Value Button Colors ── */}
      <div className="tab-section">
        <div className="tab-section-header">
          <div className="tab-section-meta">
            <h3 className="tab-section-title">Predefined Value Button Colors</h3>
            <p className="tab-section-sub">Default, hover and active state colors</p>
          </div>
        </div>
        <div className="tab-section-body">
          {renderInputRow(
            'Predefined button background color',
            'dotfw_pred_val_btn_bg_color',
            'colorPicker', [], '',
            Settings, updateSettings
          )}
          {renderInputRow(
            'Predefined button Text color',
            'dotfw_pred_val_btn_text_color',
            'colorPicker', [], '',
            Settings, updateSettings
          )}
          {renderInputRow(
            'Predefined button hover background color',
            'dotfw_pred_val_btn_bg_hover_color',
            'colorPicker', [], '',
            Settings, updateSettings
          )}
          {renderInputRow(
            'Predefined button hover text color',
            'dotfw_pred_val_btn_hover_text_color',
            'colorPicker', [], '',
            Settings, updateSettings
          )}
          {renderInputRow(
            'Predefined button active background color',
            'dotfw_pred_val_active_btn_bg_color',
            'colorPicker', [], '',
            Settings, updateSettings
          )}
          {renderInputRow(
            'Predefined button active text color',
            'dotfw_pred_val_active_btn_text_color',
            'colorPicker', [], '',
            Settings, updateSettings
          )}
        </div>
      </div>

      {/* ── Save row ── */}
      <div className="tab-save-row">
        <div className="tab-save-row-left">
          {saved && (
            <span className="tab-save-badge" aria-live="polite">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <circle cx="7" cy="7" r="6.5" fill="#d1fae5" stroke="#34d399" />
                <path d="M4 7l2 2 4-4" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              All changes saved
            </span>
          )}
        </div>

        <button
          className={`tab-save-btn${loading ? ' tab-save-btn--busy' : ''}`}
          onClick={handleSave}
          disabled={loading}
          type="button"
        >
          {loading ? (
            <>
              <span className="tab-btn-spinner" aria-hidden="true" />
              Saving…
            </>
          ) : (
            <>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                <path d="M2 7.5L6 11.5L13 4" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Save Changes
            </>
          )}
        </button>
      </div>

    </div>
  );
};

export default StyleSettingsTab;